import React, { useState } from "react";
import ButtonSuccess from "@ugg/shared/components/common/ButtonSuccess";
import { AuthInput } from "@ugg/shared/components/auth/AuthInput";
import { useEmailSignup } from "@ugg/shared/hooks/use-email-signup";
import { isValidEmail, isValidPassword, PASSWORD_REQUIREMENT } from "@ugg/shared/utils/form-validation";

const CHECKBOX_CLASS = "mt-[2px] w-[14px] h-[14px] flex-shrink-0 cursor-pointer accent-accent-blue-400";
const CHECKBOX_LABEL_CLASS = "text-[12px] text-lavender-50 font-inter leading-[16px]";

type SignupFormProps = React.PropsWithChildren<{
  onSignedUp: () => void;
  // Headline copy differs by entry point (the checkout funnel sells PLUS).
  subheading?: string;
}>;

export const SignupForm = ({ onSignedUp, subheading = "to Get Started", children }: SignupFormProps) => {
  const { step, error, isLoading, submitDetails, submitCode, resendCode, reset, captchaContainerRef } =
    useEmailSignup(onSignedUp);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const onSubmitDetails = () => {
    const trimmedEmail = email.trim();

    const nextEmailError = !trimmedEmail
      ? "Email address is required"
      : !isValidEmail(trimmedEmail)
      ? "Invalid email address"
      : "";
    const nextPasswordError = isValidPassword(password) ? "" : PASSWORD_REQUIREMENT;

    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);

    if (nextEmailError || nextPasswordError) {
      return;
    }

    submitDetails({ emailAddress: trimmedEmail, password, marketingOptIn });
  };

  const errorMessage = error && <p className="mb-[8px] text-[14px] text-accent-red-450 font-[500]">{error}</p>;

  if (step === "code") {
    return (
      <>
        <div className="mb-[36px] text-white text-[30px] font-bold font-barlow">Enter the code sent to {email}.</div>
        <form onSubmit={(e) => e.preventDefault()} className="w-full text-left">
          {errorMessage}
          <AuthInput
            value={code}
            invalid={!!error}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            tabIndex={1000}
            autoFocus
          />
          <div ref={captchaContainerRef} className="mt-5" />
          <ButtonSuccess
            className="btn-blue h-[42px] !text-[14px] mt-5"
            label="Verify"
            isLoading={isLoading}
            onClick={() => submitCode(code)}
            bForceSubmitOnEnter
          />
          <div className="mt-[20px] flex flex-col items-start gap-[8px]">
            <button type="button" className="text-[14px] text-accent-blue-400 font-bold underline" onClick={resendCode}>
              Resend code
            </button>
            <button
              type="button"
              className="text-[14px] text-accent-blue-400 font-bold underline"
              onClick={() => {
                setCode("");
                reset();
              }}
            >
              Use a different email
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <div className="text-white text-[30px] font-bold font-barlow">Create an Account</div>
      <div className="mb-[36px] text-white text-[30px] font-bold font-barlow">{subheading}</div>
      <form onSubmit={(e) => e.preventDefault()} className="w-full text-left">
        {errorMessage}
        <AuthInput
          className="mb-[12px]"
          value={email}
          error={emailError}
          invalid={!!error}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email address"
          type="email"
          autoComplete="email"
          tabIndex={1000}
          autoFocus
        />
        <AuthInput
          value={password}
          error={passwordError}
          invalid={!!error}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter a password"
          type="password"
          autoComplete="new-password"
          tabIndex={1001}
        />

        <div className="mt-[20px]">
          <label className="flex items-start gap-[10px] cursor-pointer">
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
              className={CHECKBOX_CLASS}
              tabIndex={1003}
            />
            <span className={CHECKBOX_LABEL_CLASS}>Keep me informed of any updates &amp; features</span>
          </label>
        </div>

        <div ref={captchaContainerRef} className="mt-5" />
        <ButtonSuccess
          className="btn-blue h-[42px] !text-[14px] mt-5"
          label="Sign Up"
          isLoading={isLoading}
          disable={isLoading}
          onClick={onSubmitDetails}
          bForceSubmitOnEnter
        />
        {children}
      </form>
    </>
  );
};
